<template>
  <div class="flex flex-col h-full">
    <!-- Empty state: no content record at all -->
    <div v-if="!store.currentContent.value && !store.generating.value" class="flex-1 flex items-center justify-center text-gray-600 text-sm italic">
      Generate or load content to see output here
    </div>

    <!-- Manual entry: content exists but no body yet -->
    <div v-else-if="store.currentContent.value && !rawBody && !store.generating.value" class="flex-1 flex flex-col p-4">
      <textarea
        v-model="manualEntryText"
        placeholder="Paste or write your content here..."
        class="flex-1 w-full text-sm text-gray-900 border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500 resize-none"
      ></textarea>
      <div class="mt-3 flex justify-end">
        <button
          @click="saveManualEntry"
          :disabled="!manualEntryText.trim() || savingManual"
          class="text-sm px-4 py-2 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 transition-colors"
        >{{ savingManual ? 'Saving...' : 'Save as version' }}</button>
      </div>
    </div>

    <template v-else>
      <!-- Editable title + author -->
      <div v-if="store.currentContent.value && !store.generating.value" class="px-4 pt-3 pb-0 shrink-0">
        <input
          v-model="editableTitle"
          type="text"
          placeholder="Untitled"
          class="w-full text-lg font-bold text-gray-900 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-teal-500 focus:outline-none px-0 py-1 transition-colors"
          @blur="saveTitleIfChanged"
          @keydown.enter="$event.target.blur()"
        />
        <span v-if="store.currentContent.value.author_name" class="text-xs text-gray-500">by {{ store.currentContent.value.author_name }}</span>
      </div>

      <!-- Version tabs + edit mode toggle + translate -->
      <div v-if="versions.length || rawBody" class="flex items-center gap-1 px-4 pt-2 pb-1 overflow-x-auto shrink-0">
        <button
          v-for="v in versions"
          :key="v.id"
          @click="handleVersionSwitch(v.id)"
          class="text-sm px-2 py-1 rounded whitespace-nowrap transition-colors"
          :class="activeVersionId === v.id && !draftingNewVersion
            ? 'bg-teal-100 text-teal-800 font-medium'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
        >{{ versionLabel(v) }}</button>

        <!-- Add new version button -->
        <button
          v-if="!store.generating.value"
          @click="startDraftingNewVersion"
          class="text-sm w-7 h-7 rounded flex items-center justify-center transition-colors"
          :class="draftingNewVersion
            ? 'bg-teal-100 text-teal-800'
            : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'"
          title="Add new version"
        ><Plus :size="14" /></button>

        <!-- Version star rating -->
        <div v-if="activeVersion && !store.generating.value && !draftingNewVersion" class="ml-2 flex items-center gap-0.5">
          <button
            v-for="star in 5"
            :key="star"
            @click="setVersionRating(star)"
            class="p-0 leading-none transition-colors"
            :title="`Rate ${star} star${star > 1 ? 's' : ''}`"
          >
            <Star
              :size="14"
              :class="star <= (activeVersion.rating || 0)
                ? 'fill-amber-400 text-amber-400'
                : 'text-gray-300 hover:text-amber-300'"
            />
          </button>
        </div>

        <!-- Edit mode toggle -->
        <div v-if="!store.generating.value && !draftingNewVersion" class="ml-2 flex bg-gray-100 rounded p-0.5">
          <button
            @click="editMode = 'paragraph'"
            class="text-xs px-2 py-1 rounded transition-colors"
            :class="editMode === 'paragraph'
              ? 'bg-white text-gray-900 shadow-sm font-medium'
              : 'text-gray-600 hover:text-gray-800'"
          >Paragraphs</button>
          <button
            @click="editMode = 'full'"
            class="text-xs px-2 py-1 rounded transition-colors"
            :class="editMode === 'full'
              ? 'bg-white text-gray-900 shadow-sm font-medium'
              : 'text-gray-600 hover:text-gray-800'"
          >Full edit</button>
        </div>

        <div v-if="!draftingNewVersion" class="ml-auto flex items-center gap-1 shrink-0">
          <select
            v-model="translateLanguage"
            class="text-xs border border-gray-300 rounded px-1.5 py-1 focus:outline-none focus:border-teal-400"
          >
            <option v-for="lang in languages" :key="lang" :value="lang">{{ lang }}</option>
          </select>
          <button
            @click="handleTranslate"
            :disabled="store.generating.value"
            class="text-xs px-2 py-1 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 transition-colors whitespace-nowrap"
          >Translate</button>
        </div>
      </div>

      <!-- Drafting new version -->
      <div v-if="draftingNewVersion" class="flex-1 min-h-0 flex flex-col p-4">
        <textarea
          ref="newVersionTextarea"
          v-model="newVersionText"
          placeholder="Paste or write your new version here..."
          class="flex-1 w-full text-sm text-gray-900 border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500 resize-none leading-relaxed"
        ></textarea>
      </div>

      <!-- Full edit mode -->
      <div v-else-if="editMode === 'full' && !store.generating.value" class="flex-1 min-h-0 flex flex-col p-4">
        <textarea
          ref="fullEditTextarea"
          v-model="fullEditText"
          class="flex-1 w-full text-sm text-gray-900 border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500 resize-none leading-relaxed"
        ></textarea>
      </div>

      <!-- Block-based output (paragraph edit mode) -->
      <div v-else ref="blocksContainer" class="flex-1 min-h-0 overflow-y-auto pl-4 pr-10 py-2">
        <!-- Generating: show loading indicator -->
        <div v-if="store.generating.value" class="flex items-center justify-center py-16">
          <div class="text-center">
            <div class="inline-block w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mb-3"></div>
            <p class="text-sm text-gray-600">Generating content...</p>
          </div>
        </div>

        <!-- Blocks mode: rendered after generation completes -->
        <template v-else>
          <div
            v-for="(block, index) in blocks"
            :key="block.id"
            class="group relative"
            :class="{
              'bg-amber-50 rounded': selectedBlocks.has(index),
              'hover:bg-gray-50 rounded': !selectedBlocks.has(index) && !editingBlockIndex,
            }"
          >
            <!-- Selection checkbox -->
            <button
              v-if="!editingBlockIndex"
              @click.stop="toggleBlock(index)"
              class="absolute -right-6 top-1 w-5 h-5 rounded border flex items-center justify-center text-xs transition-colors"
              :class="selectedBlocks.has(index)
                ? 'bg-amber-500 border-amber-500 text-white'
                : 'border-gray-300 text-transparent group-hover:border-gray-400 group-hover:text-gray-400'"
            >
              <Check :size="12" />
            </button>

            <!-- Block content -->
            <div
              v-if="editingBlockIndex === index"
              class="py-1"
            >
              <textarea
                ref="editTextarea"
                v-model="editingBlockContent"
                @blur="finishEditing"
                @keydown.escape="cancelEditing"
                class="w-full text-sm text-gray-900 border border-teal-400 rounded px-2 py-1 focus:outline-none resize-none"
                :rows="editingBlockContent.split('\n').length + 1"
              ></textarea>
            </div>
            <div
              v-else
              @dblclick="startEditing(index)"
              class="cursor-text py-1 px-1"
            >
              <component
                :is="blockTag(block.type)"
                :class="blockClasses(block.type)"
              >{{ block.content }}</component>
            </div>
          </div>
        </template>
      </div>

      <!-- Footer: word count + dirty indicator + save button + publish -->
      <div class="shrink-0 px-4 py-2 border-t border-gray-200 flex items-center justify-between gap-2">
        <div class="text-sm text-gray-600">
          <template v-if="draftingNewVersion">{{ newVersionWordCount }} words</template>
          <template v-else>{{ wordCount }} words</template>
        </div>
        <div class="flex items-center gap-2">
          <template v-if="draftingNewVersion">
            <button
              @click="cancelDraftingNewVersion"
              class="text-sm px-3 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
            >Cancel</button>
            <button
              @click="saveNewVersion"
              :disabled="!newVersionText.trim() || savingNewVersion"
              class="text-sm px-3 py-1 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 transition-colors"
            >{{ savingNewVersion ? 'Saving...' : 'Save as new version' }}</button>
          </template>
          <template v-else-if="isDirty && !store.generating.value">
            <span class="text-sm text-orange-600 font-medium">Unsaved edits</span>
            <button
              v-if="activeVersion"
              @click="saveCurrentVersion"
              :disabled="savingVersion"
              class="text-sm px-3 py-1 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 transition-colors"
            >{{ savingVersion ? 'Saving...' : 'Save' }}</button>
            <button
              @click="saveAsVersion"
              :disabled="savingVersion"
              class="text-sm px-3 py-1 rounded bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 transition-colors"
            >Save as new version</button>
          </template>
          <template v-if="selectedBlocks.size > 0">
            <span class="text-sm text-amber-700 font-medium">{{ selectedBlocks.size }} selected</span>
            <button @click="clearSelection" class="text-sm text-gray-500 hover:text-gray-700">Clear</button>
          </template>
          <!-- SEO button -->
          <button
            v-if="canGenerateSeo"
            @click="store.dialogMode.value = 'seo'"
            class="text-sm px-3 py-1 rounded transition-colors flex items-center gap-1.5"
            :class="seoGenerated
              ? 'bg-green-50 text-green-700 border border-green-300 hover:bg-green-100'
              : 'bg-purple-600 text-white hover:bg-purple-700'"
          >
            {{ seoGenerated ? 'SEO ready' : 'Generate SEO' }}
          </button>
          <!-- Publish button -->
          <button
            v-if="canPublish"
            @click="store.dialogMode.value = 'publish'"
            class="text-sm px-3 py-1 rounded transition-colors flex items-center gap-1.5"
            :class="store.currentContent.value?.cms_article_id
              ? 'bg-green-50 text-green-700 border border-green-300 hover:bg-green-100'
              : 'bg-indigo-600 text-white hover:bg-indigo-700'"
          >
            <span v-if="store.currentContent.value?.cms_article_id" class="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
            {{ store.currentContent.value?.cms_article_id ? 'Update CMS' : 'Publish' }}
          </button>
        </div>
      </div>

      <PublishDialog />

      <!-- Regeneration bar (when blocks selected in paragraph mode) -->
      <div v-if="editMode === 'paragraph' && selectedBlocks.size > 0 && !store.generating.value" class="shrink-0 border-t border-amber-200 bg-amber-50 px-4 py-3">
        <div class="flex flex-wrap gap-2 mb-2">
          <button
            v-for="preset in presets"
            :key="preset.key"
            @click="regenerateWithPreset(preset)"
            class="text-sm px-3 py-1.5 rounded-full border border-amber-300 bg-white text-amber-800 hover:bg-amber-100 transition-colors"
          >{{ preset.label }}</button>
        </div>
        <div class="flex gap-2">
          <input
            v-model="customInstruction"
            @keydown.enter="regenerateWithCustom"
            type="text"
            placeholder="e.g., Add more data points, stronger opening hook, tighter transitions"
            class="flex-1 text-sm border border-amber-300 rounded px-3 py-1.5 focus:outline-none focus:border-amber-500"
          />
          <button
            @click="regenerateWithCustom"
            :disabled="!customInstruction.trim()"
            class="text-sm px-3 py-1.5 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 transition-colors"
          >Regenerate</button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, inject, nextTick } from 'vue'
import { Check, Star, Plus } from 'lucide-vue-next'
import PublishDialog from './PublishDialog.vue'

const store = inject('workbench')

const activeVersionId = ref(null)
const selectedBlocks = ref(new Set())
const editingBlockIndex = ref(null)
const editingBlockContent = ref('')
const customInstruction = ref('')
const blocksContainer = ref(null)
const editTextarea = ref(null)
const fullEditTextarea = ref(null)
const newVersionTextarea = ref(null)
const translateLanguage = ref('Slovak')
const languages = ['Slovak', 'Czech', 'German', 'Spanish', 'French']
const savingVersion = ref(false)
const manualEntryText = ref('')
const savingManual = ref(false)
const editMode = ref('paragraph') // 'paragraph' | 'full'
const fullEditText = ref('')
const editableTitle = ref('')
const draftingNewVersion = ref(false)
const newVersionText = ref('')
const savingNewVersion = ref(false)

// Dirty tracking: snapshot of the body as-saved
const savedBody = ref('')

const LOCALE_MAP = { Slovak: 'sk', Czech: 'cs', English: 'en', German: 'de', Spanish: 'es', French: 'fr', Polish: 'pl', Italian: 'it' }

function versionLabel(v) {
  if (v.language && LOCALE_MAP[v.language]) {
    return `${LOCALE_MAP[v.language]}${v.version_number}`
  }
  return `v${v.version_number}`
}

const NO_SEO_FORMATS = new Set([
  'social_post', 'linkedin_post', 'twitter_thread',
  'instagram_caption', 'email', 'brief_post'
])

const canGenerateSeo = computed(() => {
  const content = store.currentContent.value
  return content?.body
    && !store.generating.value
    && !NO_SEO_FORMATS.has(content.format)
})

const seoGenerated = computed(() => {
  const m = store.currentContent.value?.cms_metadata
  return m && (m.metaTitle || m.metaDescription || m.geoQuickAnswer)
})

const presets = [
  { key: 'concise', label: 'Make more concise', instruction: 'Make these paragraphs more concise. Cut unnecessary words, tighten the prose.' },
  { key: 'detail', label: 'Add more detail', instruction: 'Add more detail and depth to these paragraphs. Include specifics, examples, or data.' },
  { key: 'formal', label: 'More formal', instruction: 'Rewrite these paragraphs in a more formal, professional tone.' },
  { key: 'casual', label: 'More casual', instruction: 'Rewrite these paragraphs in a more casual, conversational tone.' },
  { key: 'simplify', label: 'Simplify', instruction: 'Simplify the language in these paragraphs. Use shorter words and clearer sentences.' },
  { key: 'engaging', label: 'More engaging', instruction: 'Make these paragraphs more engaging and compelling. Add hooks, rhetorical devices, or vivid language.' },
]

const versions = computed(() =>
  store.currentContent.value?.content_versions || []
)

const activeVersion = computed(() =>
  versions.value.find(v => v.id === activeVersionId.value)
)

const rawBody = computed(() => {
  if (store.generating.value) return store.streamingText.value || ''
  // In full edit mode, the textarea is the source of truth
  if (editMode.value === 'full' && !store.generating.value && fullEditText.value) {
    return fullEditText.value
  }
  return activeVersion.value?.body || store.currentContent.value?.body || ''
})

// Parse markdown body into blocks
const blocks = computed(() => parseBlocks(rawBody.value))

const wordCount = computed(() => {
  const text = rawBody.value
  if (!text) return 0
  return text.trim().split(/\s+/).filter(Boolean).length
})

const newVersionWordCount = computed(() => {
  if (!newVersionText.value) return 0
  return newVersionText.value.trim().split(/\s+/).filter(Boolean).length
})

const isDirty = computed(() => {
  if (!savedBody.value && !rawBody.value) return false
  return rawBody.value !== savedBody.value
})

const canPublish = computed(() => {
  return store.currentProject.value?.cms_connection
    && store.currentContent.value?.body
    && !store.generating.value
})

function parseBlocks(markdown) {
  if (!markdown) return []
  const lines = markdown.split('\n')
  const result = []
  let currentParagraph = []
  let blockId = 0

  function flushParagraph() {
    const text = currentParagraph.join('\n').trim()
    if (text) {
      result.push({ id: `b${blockId++}`, type: 'paragraph', content: text })
    }
    currentParagraph = []
  }

  for (const line of lines) {
    const headingMatch = line.match(/^(#{1,6})\s+(.+)/)
    if (headingMatch) {
      flushParagraph()
      const level = headingMatch[1].length
      result.push({ id: `b${blockId++}`, type: `h${level}`, content: headingMatch[2] })
    } else if (line.trim() === '') {
      flushParagraph()
    } else {
      currentParagraph.push(line)
    }
  }
  flushParagraph()
  return result
}

function blocksToMarkdown(blockList) {
  return blockList.map(b => {
    if (b.type.startsWith('h')) {
      const level = parseInt(b.type[1])
      return '#'.repeat(level) + ' ' + b.content
    }
    return b.content
  }).join('\n\n')
}

function blockTag(type) {
  if (type.startsWith('h')) return type
  return 'p'
}

function blockClasses(type) {
  if (type === 'h1') return 'text-xl font-bold text-gray-900 my-2'
  if (type === 'h2') return 'text-lg font-bold text-gray-900 my-2'
  if (type === 'h3') return 'text-base font-semibold text-gray-900 my-1.5'
  if (type.startsWith('h')) return 'text-sm font-semibold text-gray-900 my-1'
  return 'text-sm text-gray-900 my-1 leading-relaxed'
}

function snapshotSavedBody() {
  savedBody.value = activeVersion.value?.body || store.currentContent.value?.body || ''
}

// Sync fullEditText when switching to full edit mode or when body changes
watch(editMode, (mode) => {
  if (mode === 'full') {
    const body = activeVersion.value?.body || store.currentContent.value?.body || ''
    fullEditText.value = body
    clearSelection()
  } else {
    // Switching back to paragraph mode: apply full edit text if changed
    if (fullEditText.value) {
      if (activeVersion.value) {
        activeVersion.value.body = fullEditText.value
      } else if (store.currentContent.value) {
        store.currentContent.value.body = fullEditText.value
      }
    }
  }
})

function handleVersionSwitch(id) {
  if (id === activeVersionId.value && !draftingNewVersion.value) return
  if (draftingNewVersion.value && newVersionText.value.trim()) {
    if (!confirm('You have an unsaved draft. Switch version and discard it?')) return
  } else if (isDirty.value) {
    if (!confirm('You have unsaved edits. Switch version and discard changes?')) return
  }
  draftingNewVersion.value = false
  newVersionText.value = ''
  activeVersionId.value = id
  store.setActiveVersion(id)
  nextTick(() => {
    snapshotSavedBody()
    // Update full edit text if in full edit mode
    if (editMode.value === 'full') {
      fullEditText.value = activeVersion.value?.body || store.currentContent.value?.body || ''
    }
  })
  clearSelection()
}

function selectVersion(id) {
  activeVersionId.value = id
  nextTick(() => {
    snapshotSavedBody()
    if (editMode.value === 'full') {
      fullEditText.value = activeVersion.value?.body || store.currentContent.value?.body || ''
    }
  })
  clearSelection()
}

function toggleBlock(index) {
  const next = new Set(selectedBlocks.value)
  if (next.has(index)) {
    next.delete(index)
  } else {
    next.add(index)
  }
  selectedBlocks.value = next
}

function clearSelection() {
  selectedBlocks.value = new Set()
  customInstruction.value = ''
}

async function handleTranslate() {
  if (store.generating.value) return
  await store.translate(translateLanguage.value, activeVersionId.value)
  // Auto-select the newly created translation version
  if (store.currentContent.value?.content_versions?.length) {
    const newId = store.currentContent.value.content_versions[0].id
    activeVersionId.value = newId
    store.setActiveVersion(newId)
    nextTick(snapshotSavedBody)
  }
}

function startEditing(index) {
  editingBlockIndex.value = index
  editingBlockContent.value = blocks.value[index].content
  nextTick(() => {
    if (editTextarea.value) {
      const el = Array.isArray(editTextarea.value) ? editTextarea.value[0] : editTextarea.value
      el?.focus()
    }
  })
}

function finishEditing() {
  if (editingBlockIndex.value === null) return
  const idx = editingBlockIndex.value
  const updated = [...blocks.value]
  updated[idx] = { ...updated[idx], content: editingBlockContent.value }
  const newBody = blocksToMarkdown(updated)

  // Update the active version body or content body
  if (activeVersion.value) {
    activeVersion.value.body = newBody
  } else if (store.currentContent.value) {
    store.currentContent.value.body = newBody
  }
  editingBlockIndex.value = null
  editingBlockContent.value = ''
}

function cancelEditing() {
  editingBlockIndex.value = null
  editingBlockContent.value = ''
}

async function saveManualEntry() {
  if (!manualEntryText.value.trim() || savingManual.value) return
  savingManual.value = true
  try {
    const version = await store.createVersion(manualEntryText.value, 'Manual entry')
    if (version) {
      // Also set body on the content so rawBody picks it up
      store.currentContent.value.body = manualEntryText.value
      activeVersionId.value = version.id
      nextTick(snapshotSavedBody)
      manualEntryText.value = ''
    }
  } finally {
    savingManual.value = false
  }
}

async function saveCurrentVersion() {
  if (!isDirty.value || savingVersion.value || !activeVersion.value) return
  savingVersion.value = true
  try {
    const body = rawBody.value
    await store.updateVersion(activeVersion.value.id, { body, change_summary: 'Manual edit' })
    nextTick(snapshotSavedBody)
  } finally {
    savingVersion.value = false
  }
}

function setVersionRating(star) {
  if (!activeVersion.value) return
  // Toggle off if clicking the same star
  const newRating = activeVersion.value.rating === star ? 0 : star
  store.rateVersion(activeVersion.value.id, newRating)
}

async function saveTitleIfChanged() {
  const content = store.currentContent.value
  if (!content) return
  const newTitle = editableTitle.value.trim()
  if (newTitle === (content.title || '')) return
  await store.saveContent({ title: newTitle || null })
}

async function saveAsVersion() {
  if (!isDirty.value || savingVersion.value) return
  savingVersion.value = true
  try {
    const body = rawBody.value
    const version = await store.createVersion(body, 'Manual edit')
    if (version) {
      activeVersionId.value = version.id
      nextTick(snapshotSavedBody)
    }
  } finally {
    savingVersion.value = false
  }
}

function startDraftingNewVersion() {
  if (draftingNewVersion.value) return
  if (isDirty.value) {
    if (!confirm('You have unsaved edits. Start a new draft and discard changes?')) return
  }
  draftingNewVersion.value = true
  newVersionText.value = ''
  nextTick(() => {
    const el = Array.isArray(newVersionTextarea.value) ? newVersionTextarea.value[0] : newVersionTextarea.value
    el?.focus()
  })
}

function cancelDraftingNewVersion() {
  if (newVersionText.value.trim() && !confirm('Discard this draft?')) return
  draftingNewVersion.value = false
  newVersionText.value = ''
}

async function saveNewVersion() {
  if (!newVersionText.value.trim() || savingNewVersion.value) return
  savingNewVersion.value = true
  try {
    const version = await store.createVersion(newVersionText.value, 'Standalone version')
    if (version) {
      store.currentContent.value.body = newVersionText.value
      activeVersionId.value = version.id
      store.setActiveVersion(version.id)
      draftingNewVersion.value = false
      newVersionText.value = ''
      nextTick(snapshotSavedBody)
    }
  } finally {
    savingNewVersion.value = false
  }
}

async function regenerateWithPreset(preset) {
  await triggerRegeneration(preset.instruction)
}

async function regenerateWithCustom() {
  if (!customInstruction.value.trim()) return
  await triggerRegeneration(customInstruction.value.trim())
}

async function triggerRegeneration(instruction) {
  const indices = Array.from(selectedBlocks.value).sort((a, b) => a - b)
  const fullBody = rawBody.value

  await store.regenerateBlocks({
    fullBody,
    blockIndices: indices,
    instruction,
    onChunk: handleRegenerationChunk,
  })
}

function handleRegenerationChunk(replacementText) {
  // After regeneration completes, the store will have the replacement text.
  // We apply it by replacing selected blocks.
  // This is handled in the store's regenerateBlocks flow.
}

// When content loads, restore saved active version or pick latest
watch(() => store.currentContent.value, (content) => {
  if (content?.content_versions?.length) {
    const saved = content.active_version_id
    const exists = saved && content.content_versions.some(v => v.id === saved)
    activeVersionId.value = exists ? saved : content.content_versions[0].id
  } else {
    activeVersionId.value = null
  }
  editableTitle.value = content?.title || ''
  clearSelection()
  nextTick(() => {
    snapshotSavedBody()
    if (editMode.value === 'full') {
      fullEditText.value = activeVersion.value?.body || store.currentContent.value?.body || ''
    }
  })
}, { immediate: true })

// When version changes via external update (new version added after generation)
watch(versions, (vs) => {
  if (vs.length && !activeVersionId.value) {
    activeVersionId.value = vs[0].id
    nextTick(snapshotSavedBody)
  }
})

// Auto-scroll during streaming
watch(() => store.streamingText.value, () => {
  if (store.generating.value && blocksContainer.value) {
    nextTick(() => {
      blocksContainer.value.scrollTop = blocksContainer.value.scrollHeight
    })
  }
})

// Snapshot savedBody after generation completes
watch(() => store.generating.value, (generating, wasGenerating) => {
  if (wasGenerating && !generating) {
    nextTick(() => {
      snapshotSavedBody()
      // Update full edit text after generation completes
      if (editMode.value === 'full') {
        fullEditText.value = activeVersion.value?.body || store.currentContent.value?.body || ''
      }
    })
  }
})

// Expose body for other components
store._getOutputBody = () => rawBody.value
store._getBlocks = () => blocks.value
store._applyBlockReplacements = (indices, newBlocksText, originalBody) => {
  // Use the captured original body to get correct blocks
  // (blocks.value is wrong here because generating=true makes rawBody return streamingText)
  const originalBlocks = parseBlocks(originalBody)
  const updated = [...originalBlocks]
  const replacementBlocks = parseBlocks(newBlocksText)

  // Replace selected blocks with regenerated ones
  // Sort indices descending so splicing doesn't shift subsequent indices
  const sortedDesc = [...indices].sort((a, b) => b - a)
  for (const idx of sortedDesc) {
    updated.splice(idx, 1)
  }
  // Insert replacements at the position of the first selected block
  const insertAt = Math.min(...indices)
  updated.splice(insertAt, 0, ...replacementBlocks)

  const newBody = blocksToMarkdown(updated)
  if (activeVersion.value) {
    activeVersion.value.body = newBody
  } else if (store.currentContent.value) {
    store.currentContent.value.body = newBody
  }
  clearSelection()
}
</script>
